import React, { useEffect } from 'react';
import { WheelSafeNumberInput } from '../common/WheelSafeNumberInput';
import {
    Create,
    SimpleForm,
    TextInput,
    BooleanInput,
    SelectInput,
    ReferenceInput,
    required,
    minValue,
    maxValue,
    Toolbar,
    SaveButton,
    useRedirect,
    useTranslate,
    useNotify,
    FormDataConsumer,
    useGetOne,
} from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { Box, Typography, Alert, Card, CardContent, IconButton, Tooltip } from '@mui/material';
import {
    EvStation,
    Build,
    BatteryChargingFull,
    LocationOn,
    Settings,
    Info,
    ArrowBack,
    QrCode,
    Language,
    PhotoCamera,
    AutoAwesome
} from '@mui/icons-material';
import { MultiLanguageFields } from '../sites/MultiLanguageFields';
import { PasswordInput } from '../common/PasswordInput';
import { ManualCodeInput } from '../common/ManualCodeInput';
import { ChargerImageUploadField } from './ChargerImageUploadField';
import { BayNumberInput } from './BayNumberInput';
import { getSuggestedCurrent, formatCurrent } from '../../utils/powerCalculations';
import { ServerErrorBinder } from '../common/ServerErrorBinder';
import { translateServerMessage } from '../../utils/translateServerMessage';

const CHARGER_KNOWN_FIELDS = new Set([
    'name', 'siteId', 'loadGroupId', 'model', 'serialNumber',
    'maxPower', 'maxCurrent', 'numberPhases', 'maxSimultaneousConnectors',
    'status', 'ocppStatus', 'errorCode', 'ocppVersion',
    'firmwareVersion', 'power', 'ocppTemplateId',
    'qrCode', 'physicalLocation', 'bayNumber', 'zone', 'stationId',
    'authUsername', 'authPassword', 'authEnabled',
    'connectorType', 'connectorCount', 'manufacturer',
    'imageUrl', 'notes', 'translations',
]);

const mapChargerBackendPath = (path: string): string =>
    path.replace(/^body\./, '').replace(/^params\./, '').replace(/^query\./, '');

const chargerFieldLabelKey = (field: string): string =>
    CHARGER_KNOWN_FIELDS.has(field)
        ? `resources.chargers.fields.${field}`
        : field;

// Component to handle max current field with auto-calculation
const MaxCurrentField = ({ formData, translate }: { formData: any, translate: any }) => {
    const { setValue, watch } = useFormContext();
    const maxCurrent = watch('maxCurrent');

    const suggestedCurrent = formData.maxPower && formData.connectorType
        ? getSuggestedCurrent(formData.maxPower, formData.connectorType)
        : null;

    const handleAutoCalculate = () => {
        if (suggestedCurrent) {
            setValue('maxCurrent', suggestedCurrent);
        }
    };

    const isAutoCalculated = suggestedCurrent && Math.abs((maxCurrent || 0) - suggestedCurrent) < 1;

    return (
        <Box sx={{ flex: 1, minWidth: 0, position: 'relative', '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
            <WheelSafeNumberInput
                source="maxCurrent"
                fullWidth
                step={0.1}
                min={0}
                max={1000}
                helperText={
                    suggestedCurrent
                        ? translate('resources.chargers.create.maxCurrentHelperTextWithSuggestion', {
                            suggested: formatCurrent(suggestedCurrent)
                        })
                        : translate('resources.chargers.create.maxCurrentHelperText')
                }
            />
            {suggestedCurrent && (
                <Tooltip title={
                    isAutoCalculated
                        ? translate('resources.chargers.create.autoCalculated')
                        : translate('resources.chargers.create.clickToAutoCalculate')
                }>
                    <IconButton
                        onClick={handleAutoCalculate}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            marginTop: '-12px', // Adjust for helper text
                            padding: '4px',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 200, 83, 0.08)',
                            }
                        }}
                    >
                        <AutoAwesome
                            sx={{
                                color: isAutoCalculated ? '#00C853' : '#9E9E9E',
                                fontSize: 20,
                            }}
                        />
                    </IconButton>
                </Tooltip>
            )}
        </Box>
    );
};

// Component for the number_phases field. When the selected load group is
// single-phase, the value is forced to 1 and the input is disabled — the
// electrical supply physically cannot drive a 3-phase charger.
const NumberPhasesField = ({ formData, translate }: { formData: any, translate: any }) => {
    const { setValue } = useFormContext();
    const loadGroupId = formData?.loadGroupId;

    const { data: loadGroup } = useGetOne(
        'load-groups',
        { id: loadGroupId },
        { enabled: !!loadGroupId },
    );

    const isSingleLoadGroup = (loadGroup as any)?.phase === 'single';

    useEffect(() => {
        if (isSingleLoadGroup) {
            setValue('numberPhases', 1, { shouldValidate: true, shouldDirty: false });
        }
    }, [isSingleLoadGroup, setValue]);

    return (
        <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
            <SelectInput
                source="numberPhases"
                choices={[
                    { id: 1, name: translate('resources.chargers.fields.numberPhasesOne') },
                    { id: 3, name: translate('resources.chargers.fields.numberPhasesThree') },
                ]}
                defaultValue={3}
                disabled={isSingleLoadGroup}
                helperText={
                    isSingleLoadGroup
                        ? translate('resources.chargers.create.numberPhasesForcedSingle')
                        : translate('resources.chargers.create.numberPhasesHelperText')
                }
                fullWidth
            />
        </Box>
    );
};

const CreateToolbar = () => {
    return (
        <Toolbar 
            sx={{ 
                backgroundColor: '#FAFAFA',
                borderTop: '1px solid #E0E0E0',
                px: 3,
                py: 2,
                mt: 4,
                '& .MuiButton-root': {
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                },
            }}
        >
            <SaveButton 
                variant="contained"
                sx={{
                    backgroundColor: '#00C853',
                    '&:hover': {
                        backgroundColor: '#00A843',
                        boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
                    },
                }}
            />
        </Toolbar>
    );
};

const PageHeader = () => {
    const redirect = useRedirect();
    const translate = useTranslate();
    
    return (
        <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            mb: 4,
        }}>
            <IconButton 
                onClick={() => redirect('/chargers')}
                size="large"
                sx={{ 
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '&:hover': {
                        backgroundColor: '#F5F5F5',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    },
                }}
                title={translate('resources.chargers.create.backToList')}
            >
                <ArrowBack />
            </IconButton>
            <Box>
                <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
                    {translate('resources.chargers.create.title')}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {translate('resources.chargers.create.subtitle')}
                </Typography>
            </Box>
        </Box>
    );
};

export const ChargersCreate = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const redirect = useRedirect();
    const [serverErrors, setServerErrors] = React.useState<Record<string, string>>({});

    // Validation rules
    const validateRequired = [required()];
    const validateMaxPower = [required(), minValue(1), maxValue(1000)];
    const validateConnectorCount = [required(), minValue(1), maxValue(10)];
    
    return (
        <Create 
            title={false}
            component="div"
            redirect="list"
            mutationMode="pessimistic"
            mutationOptions={{
                onSuccess: () => {
                    setServerErrors({});
                    notify('resources.chargers.notifications.createSuccess', { type: 'success' });
                    redirect('/chargers');
                },
                onError: (error: any) => {
                    const apiError = error?.body || error;
                    const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
                    const fieldErrors: Record<string, string> = {};
                    const summaryLines: string[] = [];
                    if (rawErrors) {
                        for (const [path, msgs] of Object.entries(rawErrors)) {
                            const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
                            const translated = translateServerMessage(rawMsg, translate);
                            const field = mapChargerBackendPath(path);
                            fieldErrors[field] = translated;
                            const label = translate(chargerFieldLabelKey(field), { _: field });
                            summaryLines.push(`• ${label}: ${translated}`);
                        }
                    }
                    setServerErrors(fieldErrors);

                    const headerKey = apiError?.errorCode
                        ? `errors.${apiError.errorCode}`
                        : 'validation.formHasErrors';
                    const header = translate(headerKey, { _: apiError?.message || 'Error' });

                    if (summaryLines.length > 0) {
                        notify(`${header}\n${summaryLines.join('\n')}`, {
                            type: 'error',
                            multiLine: true,
                            autoHideDuration: 10000,
                        });
                    } else {
                        notify(header, { type: 'error', autoHideDuration: 6000 });
                    }
                },
            }}
            sx={{
                maxWidth: '100%',
                overflow: 'hidden',
                backgroundColor: 'transparent',
                '& .RaCreate-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
        >
            <SimpleForm
                toolbar={<CreateToolbar />}
                sx={{
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                    '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                        px: { xs: 0, sm: 2 },
                        py: { xs: 1, sm: 2 },
                    },
                }}
            >
                <ServerErrorBinder errors={serverErrors} />
                <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', pb: { xs: 10, sm: 2 }, '& .MuiFormControl-root': { width: '100%' } }}>
                    <PageHeader />
                    
                    <Alert severity="info" sx={{ mb: { xs: 2, sm: 3 } }}>
                        <Typography variant="body2">
                            {translate('resources.chargers.create.infoMessage')}
                        </Typography>
                    </Alert>
                    
                    {/* Basic Information Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <EvStation sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.chargers.create.basicInfo')}
                                </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextInput 
                                    source="name" 
                                    validate={validateRequired}
                                    fullWidth
                                    helperText={translate('resources.chargers.create.nameHelperText')}
                                />
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'flex-start' }, gap: 2 }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <TextInput
                                            source="model"
                                            validate={validateRequired}
                                            fullWidth
                                            helperText={translate('resources.chargers.create.modelHelperText')}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <TextInput
                                            source="serialNumber"
                                            fullWidth
                                            helperText={translate('resources.chargers.create.serialHelperText')}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                    
                    {/* Image Upload Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <PhotoCamera sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.chargers.create.image')}
                                </Typography>
                            </Box>
                            
                            <ChargerImageUploadField
                                source="imageUrl"
                                label={translate('resources.chargers.fields.image')}
                                helperText={translate('resources.chargers.create.imageHelperText')}
                            />
                        </CardContent>
                    </Card>
                    
                    {/* Location Assignment Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <LocationOn sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.chargers.create.locationAssignment')}
                                </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <ReferenceInput source="siteId" reference="sites">
                                    <SelectInput 
                                        optionText="name" 
                                        fullWidth
                                        validate={validateRequired}
                                        helperText={translate('resources.chargers.create.siteHelperText')}
                                    />
                                </ReferenceInput>
                                <FormDataConsumer>
                                    {({ formData }) => (
                                        <ReferenceInput 
                                            source="loadGroupId" 
                                            reference="load-groups"
                                            filter={{ siteId: formData.siteId }}
                                            key={formData.siteId} // Force re-render when site changes
                                        >
                                            <SelectInput 
                                                optionText="name" 
                                                fullWidth
                                                validate={validateRequired}
                                                helperText={formData.siteId 
                                                    ? translate('resources.chargers.create.loadGroupHelperText')
                                                    : translate('resources.chargers.create.selectSiteFirst')
                                                }
                                                disabled={!formData.siteId}
                                            />
                                        </ReferenceInput>
                                    )}
                                </FormDataConsumer>
                                <TextInput
                                    source="physicalLocation"
                                    fullWidth
                                    multiline
                                    rows={2}
                                    helperText={translate('resources.chargers.create.physicalLocationHelperText')}
                                />
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <FormDataConsumer>
                                        {({ formData }) => <BayNumberInput siteId={formData.siteId} variant="create" />}
                                    </FormDataConsumer>
                                    <TextInput source="zone" sx={{ flex: 1 }} helperText={translate('resources.chargers.create.zoneHelperText')} />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Technical Specifications Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <BatteryChargingFull sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.chargers.create.technicalSpecs')}
                                </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {/* First row: Connector Type, Count, and Power Display */}
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'flex-start' }, gap: 2 }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <SelectInput
                                            source="connectorType"
                                            choices={[
                                                { id: 'CCS', name: 'CCS' },
                                                { id: 'CHAdeMO', name: 'CHAdeMO' },
                                                { id: 'Type2', name: 'Type 2' },
                                                { id: 'J1772', name: 'J1772' },
                                                { id: 'GB/T', name: 'GB/T' },
                                                { id: 'Tesla', name: 'Tesla' },
                                            ]}
                                            validate={validateRequired}
                                            fullWidth
                                            helperText={translate('resources.chargers.create.connectorTypeHelperText')}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <WheelSafeNumberInput
                                            source="connectorCount"
                                            validate={validateConnectorCount}
                                            fullWidth
                                            helperText={translate('resources.chargers.create.connectorCountHelperText')}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <TextInput
                                            source="power"
                                            validate={validateRequired}
                                            fullWidth
                                            helperText={translate('resources.chargers.create.powerDisplayHelperText')}
                                        />
                                    </Box>
                                </Box>
                                {/* Second row: Max Power and Max Current with auto-calculation */}
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'flex-start' }, gap: 2 }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <WheelSafeNumberInput
                                            source="maxPower"
                                            validate={validateMaxPower}
                                            fullWidth
                                            step={0.1}
                                            helperText={translate('resources.chargers.create.maxPowerHelperText')}
                                        />
                                    </Box>
                                    <FormDataConsumer>
                                        {({ formData }) => (
                                            <MaxCurrentField
                                                formData={formData}
                                                translate={translate}
                                            />
                                        )}
                                    </FormDataConsumer>
                                    <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }} />
                                </Box>
                                {/* Third row: Max Simultaneous Connectors + Number of Phases */}
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'flex-start' }, gap: 2 }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <WheelSafeNumberInput
                                            source="maxSimultaneousConnectors"
                                            label={translate('resources.chargers.fields.maxSimultaneousConnectors')}
                                            fullWidth
                                            min={1}
                                            max={10}
                                            step={1}
                                            defaultValue={1}
                                            helperText={translate('resources.chargers.create.maxSimultaneousConnectorsHelperText')}
                                        />
                                    </Box>
                                    <FormDataConsumer>
                                        {({ formData }) => (
                                            <NumberPhasesField
                                                formData={formData}
                                                translate={translate}
                                            />
                                        )}
                                    </FormDataConsumer>
                                    <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }} />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* OCPP Configuration Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Settings sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.chargers.create.ocppConfig')}
                                </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'flex-start' }, gap: 2 }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <SelectInput
                                            source="ocppVersion"
                                            choices={[
                                                { id: '1.6', name: 'OCPP 1.6' },
                                                { id: '2.0', name: 'OCPP 2.0' },
                                                { id: '2.0.1', name: 'OCPP 2.0.1' },
                                            ]}
                                            validate={validateRequired}
                                            fullWidth
                                            helperText={translate('resources.chargers.create.ocppVersionHelperText')}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <TextInput
                                            source="firmwareVersion"
                                            fullWidth
                                            helperText={translate('resources.chargers.create.firmwareHelperText')}
                                        />
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'flex-start' }, gap: 2 }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <ReferenceInput
                                            source="ocppTemplateId"
                                            reference="ocpp-templates"
                                            allowEmpty
                                        >
                                            <SelectInput
                                                optionText="templateName"
                                                fullWidth
                                                emptyText={translate('resources.chargers.fields.noTemplate')}
                                                helperText={translate('resources.chargers.create.ocppTemplateHelperText')}
                                            />
                                        </ReferenceInput>
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <TextInput
                                            source="stationId"
                                            validate={validateRequired}
                                            fullWidth
                                            helperText={translate('resources.chargers.create.stationIdHelperText')}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
                                        <BooleanInput
                                            source="authEnabled"
                                            defaultValue={true}
                                            helperText={translate('resources.chargers.create.authEnabledHelperText')}
                                        />
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'flex-start' }, gap: 2 }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <TextInput
                                            source="authUsername"
                                            fullWidth
                                            helperText={translate('resources.chargers.create.authUsernameHelperText')}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <PasswordInput
                                            source="authPassword"
                                            fullWidth
                                            helperText={translate('resources.chargers.create.authPasswordHelperText')}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                    
                    {/* Status Configuration Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Build sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.chargers.create.statusConfig')}
                                </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'flex-start' }, gap: 2 }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <SelectInput
                                            source="status"
                                            choices={[
                                                { id: 'available', name: 'Online' },
                                                { id: 'preparing', name: 'Preparing' },
                                                { id: 'charging', name: 'Charging' },
                                                { id: 'suspended_ev', name: 'Suspended EV' },
                                                { id: 'suspended_evse', name: 'Suspended EVSE' },
                                                { id: 'finishing', name: 'Finishing' },
                                                { id: 'reserved', name: 'Reserved' },
                                                { id: 'faulted', name: 'Faulted' },
                                                { id: 'unavailable', name: 'Unavailable' },
                                                { id: 'offline', name: 'Offline' },
                                                { id: 'maintenance', name: 'Maintenance' },
                                                { id: 'in_use', name: 'In Use' },
                                            ]}
                                            validate={validateRequired}
                                            fullWidth
                                            helperText={translate('resources.chargers.create.statusHelperText')}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <TextInput
                                            source="ocppStatus"
                                            fullWidth
                                            helperText={translate('resources.chargers.create.ocppStatusHelperText')}
                                        />
                                    </Box>
                                </Box>
                                <TextInput 
                                    source="errorCode" 
                                    fullWidth
                                    helperText={translate('resources.chargers.create.errorCodeHelperText')}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                    
                    {/* Identification Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <QrCode sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.chargers.create.identification')}
                                </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <ManualCodeInput 
                                    source="qrCode" 
                                    label={translate('resources.chargers.fields.qrCode')}
                                    helperText={translate('resources.chargers.create.qrCodeHelperText')}
                                    fullWidth
                                />
                            </Box>
                        </CardContent>
                    </Card>
                    
                    {/* Multi-language Translations Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Language sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.chargers.create.translations')}
                                </Typography>
                            </Box>
                            
                            <FormDataConsumer>
                                {({ formData }) => (
                                    <MultiLanguageFields 
                                        formData={formData}
                                        fields={[
                                            {
                                                name: 'name',
                                                label: translate('resources.chargers.fields.name'),
                                                required: true,
                                                helperText: translate('resources.chargers.multilang.nameHelperText')
                                            },
                                            {
                                                name: 'physicalLocation',
                                                label: translate('resources.chargers.fields.physicalLocation'),
                                                multiline: true,
                                                rows: 2,
                                                helperText: translate('resources.chargers.multilang.locationHelperText')
                                            },
                                            {
                                                name: 'notes',
                                                label: translate('resources.chargers.fields.notes'),
                                                multiline: true,
                                                rows: 3,
                                                helperText: translate('resources.chargers.multilang.notesHelperText')
                                            }
                                        ]}
                                    />
                                )}
                            </FormDataConsumer>
                        </CardContent>
                    </Card>
                    
                    {/* Additional Information Section */}
                    <Card sx={{ overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Info sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.chargers.create.additionalInfo')}
                                </Typography>
                            </Box>
                            
                            <Alert severity="info" sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                    {translate('resources.chargers.create.additionalInfoMessage')}
                                </Typography>
                            </Alert>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextInput 
                                    source="notes" 
                                    fullWidth
                                    multiline
                                    rows={3}
                                    helperText={translate('resources.chargers.create.notesHelperText')}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </SimpleForm>
        </Create>
    );
};
